import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Table, Tag, Button, Space, Tooltip, Popconfirm, Typography, Select, message } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { authenticatedFetch } from "@/libs/api";
import {
  STATUS_META, TARGET_LABELS, MODE_META, RETRYABLE, retrySyncTask, retryConfirmText, formatTime,
} from "./syncStatus";

const { Text } = Typography;

export const PROBLEM_STATUSES = ["FAILED", "BLOCKED", "NEEDS_CHECK"];

/**
 * System Onboard → Sync tab: downstream sync rows (what approvals set off outside the
 * platform). Opens on the rows that need an admin: Failed, Missing data, Check manually.
 * Superadmin only — the Onboard page shows the tab only to SUPERADMIN, and the endpoints
 * refuse anyone else.
 *
 * @param {function} [onChanged] called after a load or a retry, so the tab badge can refresh
 */
export default function DownstreamSyncTasks({ onChanged }) {
  const navigate = useNavigate();
  const [statuses, setStatuses] = useState(PROBLEM_STATUSES);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [data, setData] = useState({ rows: [], total: 0, counts: {}, targets: [] });
  const [loading, setLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ status: statuses.join(","), page: String(page), pageSize: String(pageSize) });
      const res = await authenticatedFetch(`/api/downstream-sync/tasks?${qs}`);
      const json = await res.json();
      if (json.success) setData(json.data);
      else message.error(json.message || "Failed to load");
    } catch (err) {
      message.error("Failed to load onboard sync");
    } finally {
      setLoading(false);
    }
  }, [statuses, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRetry = async (task) => {
    setRetryingId(task.id);
    try {
      const result = await retrySyncTask(task.id);
      message.success(`#${task.id}: now ${STATUS_META[result.to]?.label || result.to}`);
      fetchData();
      if (onChanged) onChanged();
    } catch (err) {
      message.error(err.message);
    } finally {
      setRetryingId(null);
    }
  };

  const columns = [
    { title: "#", dataIndex: "id", key: "id", width: 60 },
    {
      title: "System",
      key: "system",
      width: 140,
      render: (_, r) => (
        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate(`/systems/${r.sn}`)}>
          {r.sn || r.system_id}
        </Button>
      ),
    },
    { title: "Partner", dataIndex: "company_name", key: "company_name", width: 140, render: (v) => v || "—" },
    { title: "Customer", dataIndex: "customer_name", key: "customer_name", width: 140, render: (v) => v || "—" },
    { title: "Downstream", dataIndex: "target", key: "target", width: 140, render: (t) => TARGET_LABELS[t] || t },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (s) => (
        <Tooltip title={STATUS_META[s]?.help}>
          <Tag color={STATUS_META[s]?.color}>{STATUS_META[s]?.label || s}</Tag>
        </Tooltip>
      ),
    },
    {
      title: "Reason",
      dataIndex: "last_error",
      key: "last_error",
      width: 360,
      render: (v, r) => <Text style={{ fontSize: 12 }}>{v || (r.status === "DONE" ? `APP user id ${r.external_id}` : "—")}</Text>,
    },
    { title: "Updated", dataIndex: "update_time", key: "update_time", width: 150, render: (v) => <Text style={{ fontSize: 12 }}>{formatTime(v)}</Text> },
    {
      title: "",
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, r) => (RETRYABLE.includes(r.status) ? (
        <Popconfirm
          title={`Retry #${r.id}?`}
          description={<div style={{ maxWidth: 280 }}>{retryConfirmText(r)}</div>}
          onConfirm={() => handleRetry(r)}
          okText="Retry"
        >
          <Button size="small" loading={retryingId === r.id}>Retry</Button>
        </Popconfirm>
      ) : null),
    },
  ];

  return (
    <Card>
      <Space wrap style={{ marginBottom: 16 }}>
        <Text type="secondary">What approvals set off outside the platform:</Text>
        {data.targets.map((t) => (
          <Tag key={t.target} color={t.paused ? "error" : MODE_META[t.mode]?.color}>
            {TARGET_LABELS[t.target] || t.target}: {t.paused ? `Paused — ${t.paused.reason}` : MODE_META[t.mode]?.label || t.mode}
            {t.openStates ? ` · open states: ${t.openStates.join(", ")}` : ""}
          </Tag>
        ))}
        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate("/configuration/onboarding-config")}>
          Settings
        </Button>
        <Select
          mode="multiple"
          style={{ minWidth: 360 }}
          value={statuses}
          onChange={(v) => { setStatuses(v.length ? v : PROBLEM_STATUSES); setPage(1); }}
          options={Object.entries(STATUS_META).map(([value, m]) => ({
            value,
            label: `${m.label}${data.counts[value] ? ` (${data.counts[value]})` : ""}`,
          }))}
        />
        <Button icon={<ReloadOutlined spin={loading} />} onClick={() => { fetchData(); if (onChanged) onChanged(); }}>Refresh</Button>
      </Space>

      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={data.rows}
        scroll={{ x: 1330 }}
        pagination={{
          current: page,
          pageSize,
          total: data.total,
          showSizeChanger: true,
          onChange: (p, s) => { setPage(p); setPageSize(s); },
        }}
        locale={{ emptyText: "Nothing in these statuses." }}
      />
    </Card>
  );
}
